import { Menu, Transition } from "@headlessui/react";
import {
  ArrowsRightLeftIcon,
  Bars3Icon,
  BookOpenIcon,
  EnvelopeIcon,
  MegaphoneIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { useBreakingChanges, useChangelog } from "@via/api/configuration.tsx";
import Breadcrumbs from "@via/components/breadcrumbs/breadcrumbs.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useLogout } from "@via/api/auth.api.tsx";
import { ACCOUNT, ElementSize, USER_ROLES } from "@via/utils/constants.tsx";
import {
  classNames,
  shouldBlockAccess,
  shouldBlockOrgMemberAccess,
} from "@via/utils/helpers.tsx";
import { Fragment, type JSX, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { Avatar } from "@via/components/avatar.tsx";
import MobileMenu from "@via/components/navigation/mobile-menu.tsx";
import { getDocsUrl } from "@via/utils/config.tsx";
import "./navigation.css";
import { ProductUpdatesPanel } from "@via/components/navigation/product-updates-panel.tsx";
import { WorkspaceAttentionBadge } from "@via/components/navigation/workspace-attention-badge.tsx";
import { SparklesIcon } from "@heroicons/react/16/solid";
import { useUpgradeModal } from "@via/hooks/useUpgradeModal.tsx";
import { UserRole } from "common";
import CortexButton from "@via/components/cortex-button/cortex-button.tsx";
import SystemBanner from "@via/components/system-banner/system-banner.tsx";

export default function Header(): JSX.Element {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [accountOptions, setAccountOptions] = useState(ACCOUNT);
  const { user, config } = useAuth();
  const { showUpgradeModal } = useUpgradeModal();
  const { mutate: logout } = useLogout();
  const { pathname } = useLocation();
  const { data: changelog } = useChangelog();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;
  const { data: breakingChanges } = useBreakingChanges(isAdmin);
  const handleLogOut = (): void => {
    logout();
  };

  const docsLink = getDocsUrl();

  const helpMenuItems = [
    {
      name: "Documentation",
      link: docsLink,
      icon: <BookOpenIcon className="h-5 w-5" />,
      dataTestId: "documentationMenuItem",
    },
    {
      name: "Contact Support",
      link: config?.CONTACT_SUPPORT_URL,
      icon: <EnvelopeIcon className="h-5 w-5" />,
      dataTestId: "contactSupportMenuItem",
    },
    {
      name: "Customer Portal",
      link: config?.CUSTOMER_PORTAL_URL,
      icon: <UserCircleIcon className="h-5 w-5" />,
      dataTestId: "customerPortalMenuItem",
    },
  ];

  useEffect(() => {
    if (user) {
      // Filter out Admin access based on userRole. We do this at the route level also.
      const filtered = ACCOUNT.filter(item => {
        return !(
          user.userRole !== USER_ROLES.ADMIN &&
          item.requiredRole === USER_ROLES.ADMIN
        );
      });
      setAccountOptions(filtered);
    }
  }, [user]);

  useEffect(() => {
    // Automatically close the mobile menu once a route change is detected
    setMobileSidebarOpen(false);
  }, [pathname]);

  const blockAccess = shouldBlockAccess(user);
  const blockOrgMemberAccess = shouldBlockOrgMemberAccess(user);
  const shouldShowHeader = Boolean(
    user?.session && !blockAccess && !blockOrgMemberAccess
  );

  const handleUpgradeAccount = () => {
    showUpgradeModal();
  };

  return (
    <>
      {shouldShowHeader && (
        <>
          <MobileMenu
            mobileSidebarOpen={mobileSidebarOpen}
            setMobileSidebarOpen={setMobileSidebarOpen}
          />
          <header className="header justify-between border-b border-gray-200 bg-slate-50 sm:gap-x-4">
            <button
              type="button"
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
              onClick={() => {
                setMobileSidebarOpen(true);
              }}>
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="crumbs-navigation items-center justify-between sm:hidden lg:flex">
              <Breadcrumbs />
            </div>

            {/* Header (Search Bar & Account Settings Dropdown */}
            <div className="flex items-center gap-x-4 lg:gap-x-4">
              {/* System Banner */}
              <SystemBanner />
              {user?.userRole === UserRole.Guest && (
                <button
                  onClick={handleUpgradeAccount}
                  className="inline-flex items-center gap-2 rounded-md bg-indigo-600 mr-4 my-1 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors duration-200">
                  <SparklesIcon className="h-5 w-5" aria-hidden="true" />
                  Upgrade Account
                </button>
              )}
              <ul className="group flex cursor-pointer items-center gap-x-3 rounded-md py-2 text-xs font-semibold leading-6 text-gray-500">
                {config?.FOUNDRY_CLASSIC_BUTTON_URL && (
                  <a
                    href={config?.FOUNDRY_CLASSIC_BUTTON_URL}
                    className="inline-flex items-center justify-center rounded-md px-3 text-sm font-semibold text-indigo-600 duration-200 ease-in-out hover:text-indigo-500">
                    <ArrowsRightLeftIcon className="mr-2 h-4 w-4" />
                    Foundry Classic
                  </a>
                )}
                {(user?.featureFlags?.FF_AI_REPORT_INTERPRET ||
                  user?.featureFlags?.FF_CLAUDE_CHANNEL) && <CortexButton />}
                <Menu as="li" className="relative flex items-center">
                  <Menu.Button
                    id="help"
                    data-testid="helpButton"
                    className="duration-150 ease-in-out hover:text-gray-700">
                    <QuestionMarkCircleIcon className="h-5 w-5" />
                  </Menu.Button>
                  <Tooltip
                    anchorSelect={"#help"}
                    content={"Help & Support"}
                    place="bottom-end"
                  />
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95">
                    <Menu.Items className="absolute right-0 top-8 z-50 w-48 max-h-[80vh] overflow-y-auto origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none cursor-default">
                      <div className="px-4 py-2">
                        {helpMenuItems
                          .filter(item => item.link)
                          .map(item => (
                            <Menu.Item key={item.name}>
                              <Link
                                to={item.link}
                                target="_blank"
                                className="flex flex-row items-center border-none px-3 py-2 text-sm leading-6 text-gray-700 hover:bg-gray-100"
                                data-testid={item.dataTestId}>
                                {item.icon}
                                <span className="ml-2 font-bold text-[13px]">
                                  {item.name}
                                </span>
                              </Link>
                            </Menu.Item>
                          ))}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
                <WorkspaceAttentionBadge />
                <Menu as="li" className="relative flex items-center">
                  <Menu.Button
                    id="updates"
                    data-testid="updatesButton"
                    className="duration-150 ease-in-out hover:text-gray-700">
                    <MegaphoneIcon className="h-5 w-5" />
                  </Menu.Button>
                  <Tooltip
                    anchorSelect={"#updates"}
                    content={"Product Updates"}
                    place="bottom-start"
                  />
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95">
                    <Menu.Items className="absolute right-0 top-8 z-50 w-96 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none cursor-default ">
                      <ProductUpdatesPanel
                        changelogBody={changelog?.body}
                        breakingChangesBody={breakingChanges?.body}
                        isAdmin={isAdmin}
                      />
                    </Menu.Items>
                  </Transition>
                </Menu>
              </ul>
              {/* Separator */}
              <div
                className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200"
                aria-hidden="true"
              />

              {/* Profile dropdown */}
              <Menu as="div" className="relative">
                <Menu.Button
                  id="account-dropdown"
                  className="-m-1.5 flex items-center p-1.5">
                  <span className="sr-only">Open user menu</span>
                  <span className="relative inline-block">
                    <Avatar name={user.name} notifications={false} />
                  </span>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95">
                  <Menu.Items className="absolute right-0 z-50 mt-1 w-72 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                    <div className="flex flex-col items-center justify-center">
                      <span className="pb-1 pt-2">
                        <Avatar
                          name={user.name}
                          notifications={false}
                          size={ElementSize.Large}
                        />
                      </span>
                      <span className="py-1 text-sm font-medium">
                        {user.name}
                      </span>
                      <span className="py-1 text-sm">{user.email}</span>
                      <span className="pb-4 text-xs capitalize text-gray-600">
                        {user.userRole}
                      </span>
                    </div>
                    {accountOptions.map((item, index) => (
                      <Menu.Item key={item.name}>
                        {({ active }) => (
                          <Link
                            to={item.href}
                            className={classNames(
                              index === 0
                                ? "border-t-[1px] border-t-gray-100"
                                : "",
                              active ? "bg-gray-50" : "",
                              "block px-4 py-2 text-sm leading-6 text-gray-900"
                            )}>
                            {item.name}
                          </Link>
                        )}
                      </Menu.Item>
                    ))}
                    <span
                      onClick={() => {
                        handleLogOut();
                      }}
                      className="block cursor-pointer border-t-[1px] border-t-gray-100 px-4 py-2 text-sm leading-6 text-gray-900 hover:bg-gray-50">
                      Log Out
                    </span>
                    <span className="flex flex-row justify-center border-t-[1px] border-t-gray-100 pb-2 pt-3 text-xs text-gray-500">
                      {config?.VERSION}
                    </span>
                    {(config?.STATUS_PAGE_URL ||
                      config?.PRIVACY_POLICY_URL) && (
                      <span className="flex flex-row justify-center pb-2 pt-3 text-xs capitalize text-gray-500">
                        {config?.STATUS_PAGE_URL && (
                          <Link
                            to={config.STATUS_PAGE_URL}
                            target="_blank"
                            className="via-link">
                            Foundry Status
                          </Link>
                        )}
                        {config?.STATUS_PAGE_URL &&
                          config?.PRIVACY_POLICY_URL && <>&nbsp;•&nbsp;</>}
                        {config?.PRIVACY_POLICY_URL && (
                          <Link
                            to={config.PRIVACY_POLICY_URL}
                            target="_blank"
                            className="via-link">
                            Privacy Policy
                          </Link>
                        )}
                      </span>
                    )}
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </header>
        </>
      )}
    </>
  );
}
